import {
  Button,
  ConfirmModal,
  OverflowTooltip,
  TablePaginationContext,
  UnLink,
  UserAvatar,
} from '@/components';
import { RoleColumn } from '@/components/Columns/RoleColumn';
import { PagedTable } from '@/components/PagedTable/PagedTable';
import { SortableColumnDef } from '@/components/TableV2/SortableTable';
import { ToggleColumn } from '@/components/ToggleColumn';
import { RoutePath } from '@/core/router';
import { useToggleUserAccess } from '@/hooks/use-toggle-user-access';
import { useToggleUserStatus } from '@/hooks/use-toggle-user-status';
import { useDisassociateUser } from '@/modules/Tasks/queries/disassociate-user';
import { TaskUser } from '@/modules/Users/domain';
import { CreateTaskUserModal } from '@/modules/Users/pages/UsersPage/components/CreateTaskUserModal/CreateTaskUserModal';
import { useGetUsersByTask } from '@/modules/Users/queries';
import { ReactElement, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

type TaskUsersTabProps = {
  taskId: number;
  businessAreaId?: number;
};

export const TaskUsersTab = ({
  taskId,
  businessAreaId,
}: TaskUsersTabProps): ReactElement => {
  const { t } = useTranslation();
  const [isCreateTaskUserModalOpen, setIsCreateTaskUserModalOpen] =
    useState(false);
  const navigate = useNavigate();
  const { page, tableSort, searchTerm } = useContext(TablePaginationContext);
  const sort = tableSort[0];
  const { data: users, isLoading: isLoadingUsers } = useGetUsersByTask({
    taskId,
    filter: searchTerm,
    pageNumber: page,
    sortBy: sort?.columnId,
    sortOrder: sort?.direction,
  });

  const { mutate: disassociateUserTask } = useDisassociateUser();

  const [selectedDisassociationUserId, setSelectedDisassociationUserId] =
    useState<number>();

  const { onChange: onAccessChange } = useToggleUserAccess();
  const { onChange: onStatusChange } = useToggleUserStatus();

  const columns = useMemo<SortableColumnDef<TaskUser>[]>(
    () => [
      {
        id: 'name',
        name: t('name'),
        accessor: 'fullName',
        alignment: 'left',
        tdClassNames: 'max-w-[15vh]',
        renderRow: (row) => {
          return (
            <div
              className="flex items-center gap-2.5 overflow-hidden"
              onClick={() => navigate(RoutePath.userDetail(row.id.toString()))}
            >
              <UserAvatar
                size="small"
                name={row.fullName}
                imageId={row.profileImageFileId}
              />
              <OverflowTooltip text={row.fullName} textClassName="font-black" />
            </div>
          );
        },
      },
      {
        id: 'email',
        name: t('email'),
        accessor: 'email',
      },
      {
        id: 'status',
        name: t('status'),
        accessor: 'status',
        renderRow: (row) => (
          <ToggleColumn
            onChange={() => onStatusChange(!row.status, row.id)}
            row={row}
            value={row.status}
          />
        ),
      },
      {
        id: 'access',
        name: t('access'),
        accessor: 'access',
        renderRow: (row) => (
          <ToggleColumn
            onChange={() => onAccessChange(!row.access, row.id)}
            row={row}
            value={row.access}
          />
        ),
      },
      {
        id: 'role',
        name: t('role'),
        accessor: 'roleName',
        renderRow: (row) => <RoleColumn roleName={row.roleName} />,
      },
      {
        id: 'actions',
        name: '',
        renderRow: (row) => (
          <UnLink
            data-testid={'unlink-button'}
            className="cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedDisassociationUserId(row.id);
            }}
          />
        ),
      },
    ],
    [navigate, onAccessChange, onStatusChange, t],
  );

  return (
    <>
      <PagedTable
        title={t('users')}
        columns={columns}
        page={users}
        isLoading={isLoadingUsers}
        emptyStateMessage={t('no_users_available')}
        actions={
          <Button
            onClick={() => setIsCreateTaskUserModalOpen(true)}
            variant="primary"
          >
            {t('add')}
          </Button>
        }
      />
      <CreateTaskUserModal
        isOpen={isCreateTaskUserModalOpen}
        onClose={() => setIsCreateTaskUserModalOpen(false)}
        taskId={taskId}
        businessAreaId={businessAreaId}
      />
      <ConfirmModal
        isOpen={!!selectedDisassociationUserId}
        handleClose={() => setSelectedDisassociationUserId(undefined)}
        title={t('please_confirm_action')}
        message={t('confirm_user_task_disassociate')}
        onConfirm={() => {
          if (selectedDisassociationUserId) {
            disassociateUserTask({
              taskId,
              userId: selectedDisassociationUserId,
            });
          }
          setSelectedDisassociationUserId(undefined);
        }}
      />
    </>
  );
};
